import AddIcon from '@mui/icons-material/Add';
import { Box, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Cell } from 'react-table';
import { IOurEntityBankForm, ourEntityBankList } from 'wisestep-api';
import {
  WsButton,
  WsDataTable,
  WsElementWithPopOver,
  WsIconEdit,
  WsIconMoreVer,
} from 'wisestep-base';

import IconTextLink from '../../../components/IconTextLink';
import { useDrawer } from '../../../hooks/useDrawer';
import { OurEntityAction, useOurEntityAuthz } from '../../../hooks/useOurEntityAuthz';
import IOurEntityBank from '../types/IOurEntityBank';

import OurEntityBankDetail from './OurEntityBankDetail';

const pageSize = 10;

export const ShowOurEntityBanking = ({ ourEntityId }: { ourEntityId: string }) => {
  const isAuthorizedEditBanking = useOurEntityAuthz(
    OurEntityAction.EDIT_ENTITY_BANKING_INFORMATION
  );
  const isAuthorizedAddBanking = useOurEntityAuthz(OurEntityAction.ADD_ENTITY_BANKING_INFORMATION);
  const [openDrawer, closeDrawer] = useDrawer();
  const [ourEntityBankListData, setOurEntityBankListData] = useState<IOurEntityBankForm[]>([]);
  const [selectedOurEntityBankListData, setSelectedOurEntityBankListData] =
    useState<IOurEntityBankForm>();

  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [getDefaultPresent, setDefaultPresent] = useState<boolean>(true);

  const [selectedOurEntityBank, setSelectedOurEntityBank] = useState<IOurEntityBank>({
    data: {
      id: '',
      bankName: '',
      branchName: '',
      bankCode: '',
      bankAccountNumber: '',
      currency: '',
      iban: '',
      swiftCode: '',
      ourEntityId: ourEntityId ?? '',
      isDefault: false,
    },
    isEditable: true,
  });
  const getOurEntityBankDataList = useCallback(
    async (pageNo: number) => {
      const response = await ourEntityBankList(ourEntityId, pageNo, pageSize);
      setTotalRecord(response.data.totalRecord);
      setOurEntityBankListData(response.data.data);
      const hasTrueDefault = response.data.data.some((item) => item.isDefault === true);
      if (hasTrueDefault) {
        setDefaultPresent(false);
      }
    },
    [ourEntityId]
  );

  useEffect(() => {
    getOurEntityBankDataList(0);
  }, [getOurEntityBankDataList]);

  const onBankSelect = (index: number) => {
    setSelectedOurEntityBankListData(ourEntityBankListData[index]);
    const bankDataObj: IOurEntityBank = {
      data: {
        ...ourEntityBankListData[index],
        ourEntityId: ourEntityId,
        id: ourEntityBankListData[index].id ?? '',
      },
      isEditable: true,
    };
    setSelectedOurEntityBank(bankDataObj);
    openBankDrawer(true, bankDataObj);
  };

  const openBankDrawer = (editFlag = false, bankDataObj) => {
    openDrawer({
      children: (
        <OurEntityBankDetail
          ourEntityId={ourEntityId}
          listPage={true}
          loadTabData={getOurEntityBankDataList}
          showModal={closeDrawer}
          editObj={bankDataObj}
          editFlag={editFlag}
          selectedBankListRecord={selectedOurEntityBankListData}
          isDefaultPresent={getDefaultPresent}
        />
      ),
      props: {
        title: 'Add/Edit Banking Information',
      },
    });
  };

  const columnsBank = [
    {
      Header: 'Bank Name',
      accessor: 'bankName',
    },
    {
      Header: 'Currency',
      accessor: 'currency',
    },
    {
      Header: 'Branch Name',
      accessor: 'branchName',
    },
    {
      Header: 'Bank Code',
      accessor: 'bankCode',
    },
    {
      Header: 'Account Number',
      accessor: 'bankAccountNumber',
    },
    {
      Header: 'International Bank Number',
      accessor: 'iban',
    },
    {
      Header: 'Swift Code',
      accessor: 'swiftCode',
    },
    {
      Header: 'Is Default',
      accessor: 'isDefault',
      Cell: (cell: Cell) => (cell.value ? 'Yes' : 'No'),
    },
    {
      Header: '',
      accessor: 'Action',
      Cell: (cell: Cell) =>
        isAuthorizedEditBanking && (
          <WsElementWithPopOver element={<WsIconMoreVer />}>
            <Grid p={2} columnGap={1}>
              <>
                <IconTextLink
                  text="Edit Bank Information"
                  onClick={() => onBankSelect(cell.row.index)}
                  icon={<WsIconEdit />}
                />
              </>
            </Grid>
          </WsElementWithPopOver>
        ),
    },
  ];

  return (
    <Box borderRadius={1.5}>
      <Grid>
        {isAuthorizedAddBanking && (
          <Grid item xs={12} display="flex" justifyContent="flex-end" pt={2} pb={2} columnGap={2}>
            <WsButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                openBankDrawer(false, selectedOurEntityBank);
              }}
            >
              Add Banking Information
            </WsButton>
          </Grid>
        )}
        <WsDataTable
          columns={columnsBank}
          data={ourEntityBankListData as []}
          boldItems={['name']}
          pageable
          totalDataItems={totalRecord}
          fetchPage={(page) => getOurEntityBankDataList(page)}
        />
      </Grid>
    </Box>
  );
};
